/**
 * SpeedToContractSection — solicitation→signing percentile distribution.
 *
 * Phase B3 migration: internals swapped from Recharts BarChart to
 * `BoxplotRenderer` (ECharts). The mart already returns p25/p50/p75/p90 per
 * (FY, office, extent_competed, idv_type), so we aggregate to one row per FY
 * (weighting each percentile by `awards_with_solicitation_link`) and let the
 * boxplot renderer draw the distribution shape directly.
 */
import { useMemo } from 'react'
import { govdataAPI } from '../../services/govdata'
import { useGovdata } from './useGovdata'
import SectionCard from './SectionCard'
import BoxplotRenderer from '../exec/generic/BoxplotRenderer'
import type { DashboardFilters } from '../../types/govdata'
import type { PerfRow } from '../exec/generic/types'

interface Props {
  filters: DashboardFilters
  /** When true, the SectionCard fills its parent (flex-col, chart in
   *  flex-1 child) instead of imposing a fixed height — used when this
   *  component is embedded inside a dashboard widget tile. */
  embedded?: boolean
}

export default function SpeedToContractSection({ filters, embedded = false }: Props) {
  const paramsKey = JSON.stringify(filters)
  const { data, loading, error } = useGovdata(
    () => govdataAPI.speedToContract(filters),
    paramsKey,
  )

  const rows: PerfRow[] = useMemo(() => {
    // Aggregate across offices/competition-buckets by FY, weighting percentiles
    // by the count of awards with a SAM Opportunities link (only that subset
    // contributes to the timing distribution).
    const byFY = new Map<number, { n: number; p25: number; p50: number; p75: number; p90: number }>()
    for (const r of data) {
      const fy = r.fiscal_year
      const n = r.awards_with_solicitation_link || 0
      if (n === 0) continue
      const e = byFY.get(fy) || { n: 0, p25: 0, p50: 0, p75: 0, p90: 0 }
      e.n += n
      e.p25 += (r.p25_solicitation_to_award || 0) * n
      e.p50 += (r.p50_solicitation_to_award || 0) * n
      e.p75 += (r.p75_solicitation_to_award || 0) * n
      e.p90 += (r.p90_solicitation_to_award || 0) * n
      byFY.set(fy, e)
    }
    return Array.from(byFY.entries())
      .map(([fy, e]) => ({
        fiscal_year: fy,
        p25_solicitation_to_award: e.n > 0 ? Math.round(e.p25 / e.n) : 0,
        p50_solicitation_to_award: e.n > 0 ? Math.round(e.p50 / e.n) : 0,
        p75_solicitation_to_award: e.n > 0 ? Math.round(e.p75 / e.n) : 0,
        p90_solicitation_to_award: e.n > 0 ? Math.round(e.p90 / e.n) : 0,
      }))
      .sort((a, b) => a.fiscal_year - b.fiscal_year)
  }, [data])

  // The endpoint serves two non-comparable populations; the title reflects
  // which one this widget requested (config.is_ot → filters.is_ot).
  const isOt = filters.is_ot === true
  return (
    <SectionCard
      title={isOt ? 'OT Speed to Award' : 'Speed to Contract'}
      subtitle={
        isOt
          ? 'Other Transactions — solicitation → signing distribution by FY (P25 / median / P75 / P90)'
          : 'Solicitation-posting → signing distribution by FY (P25 / median / P75 / P90)'
      }
      loading={loading}
      error={error}
      embedded={embedded}
    >
      {/* In embedded mode SectionCard wraps children in flex-1 min-h-0 so
          the chart fills the remaining tile space; on the standalone page
          we fall back to a fixed height. */}
      <div className={embedded ? 'h-full w-full' : 'h-72'}>
        <BoxplotRenderer data={rows} groupBy="fiscal_year" />
      </div>
    </SectionCard>
  )
}
